import type { DetectedIssue } from "@/ai/selfHealing/runtimeMonitor";
import { logger } from "@/system/logger";

/* =========================================================
   🧠 TYPES
   ========================================================= */

// ✅ FIX TS2678: added add_field, delete_page, add_auth_guard,
// add_rate_limit to match the new actions in repairEngine.ts
export type FixActionType =
  | "create_model"
  | "update_model"
  | "add_field"
  | "create_page"
  | "update_page"
  | "delete_page"
  | "create_workflow"
  | "link_workflow"
  | "install_dependency"
  | "fix_code"
  | "add_auth_guard"
  | "add_rate_limit"
  | "manual_review";

export interface FixStep {
  order:       number;
  action:      FixActionType;
  target:      string;
  description: string;
  meta?:       Record<string, any>;
}

export interface FixPlan {
  issueId:         string;
  steps:           FixStep[];
  estimatedImpact: "low" | "medium" | "high";
  requiresReview:  boolean;
  autoExecutable:  boolean;
  category:        string;
}

/* =========================================================
   🧠 CORE PLANNER
   ========================================================= */

export function generateFixPlan(issue: DetectedIssue): FixPlan {
  const steps: FixStep[] = [];

  try {
    switch (issue.category) {

      /* ── AUTH ── */
      case "auth":
        steps.push(
          {
            order:       1,
            action:      "create_model",
            target:      "User",
            description: "Create User model with auth fields",
            meta:        { fields: ["id", "email", "password_hash", "role", "created_at"] },
          },
          {
            order:       2,
            action:      "add_auth_guard",
            target:      "protected_routes",
            description: "Add auth guard middleware to protected routes",
          },
          {
            order:       3,
            action:      "create_page",
            target:      "Login",
            description: "Generate login UI with form validation",
          },
          {
            order:       4,
            action:      "create_workflow",
            target:      "auth_flow",
            description: "Implement authentication logic with JWT",
          },
          {
            order:       5,
            action:      "link_workflow",
            target:      "Login",
            description: "Connect login UI to auth workflow",
          }
        );
        break;

      /* ── DATABASE ── */
      case "database":
        if (issue.title.startsWith("Empty model")) {
          steps.push({
            order:       1,
            action:      "update_model",
            target:      issue.title,
            description: issue.suggestedFix || "Add required fields to model",
          });
        } else if (issue.title.startsWith("Missing ID")) {
          steps.push({
            order:       1,
            action:      "add_field",
            target:      issue.title,
            description: "Add primary UUID id field",
            meta:        { field: { name: "id", type: "uuid", indexed: true } },
          });
        } else if (issue.title.startsWith("Missing timestamps")) {
          steps.push(
            {
              order:       1,
              action:      "add_field",
              target:      issue.title,
              description: "Add created_at timestamp",
              meta:        { field: { name: "created_at", type: "datetime" } },
            },
            {
              order:       2,
              action:      "add_field",
              target:      issue.title,
              description: "Add updated_at timestamp",
              meta:        { field: { name: "updated_at", type: "datetime" } },
            }
          );
        } else {
          steps.push({
            order:       1,
            action:      "update_model",
            target:      issue.title,
            description: issue.suggestedFix || "Fix database schema issue",
          });
        }
        break;

      /* ── FORM ── */
      case "form":
        steps.push(
          {
            order:       1,
            action:      "create_workflow",
            target:      "form_handler",
            description: "Create form submission handler with validation",
          },
          {
            order:       2,
            action:      "link_workflow",
            target:      issue.title,
            description: "Connect form page to handler workflow",
          }
        );
        break;

      /* ── ROUTING ── */
      case "routing":
        if (issue.title.includes("No pages")) {
          steps.push({
            order:       1,
            action:      "create_page",
            target:      "Home",
            description: "Create initial Home page as entry point",
          });
        } else if (issue.title.includes("dashboard")) {
          steps.push({
            order:       1,
            action:      "create_page",
            target:      "Dashboard",
            description: "Create Dashboard landing page",
          });
        } else if (issue.title.includes("404")) {
          steps.push({
            order:       1,
            action:      "create_page",
            target:      "NotFound",
            description: "Create 404 Not Found error page",
          });
        } else {
          steps.push({
            order:       1,
            action:      "manual_review",
            target:      "routing",
            description: issue.suggestedFix || "Review routing configuration",
          });
        }
        break;

      /* ── SECURITY ── */
      case "security":
        if (issue.title.includes("plaintext password")) {
          steps.push(
            {
              order:       1,
              action:      "add_field",
              target:      "User",
              description: "Replace password field with password_hash",
              meta:        { field: { name: "password_hash", type: "string" } },
            },
            {
              order:       2,
              action:      "fix_code",
              target:      "auth_service",
              description: "Update auth service to use bcrypt hashing",
              meta:        { useAiFixer: true, error: issue.description },
            }
          );
        } else if (issue.title.includes("SSL")) {
          steps.push({
            order:       1,
            action:      "add_rate_limit",
            target:      "payment_routes",
            description: "Add rate limiting to payment endpoints",
          });
        } else {
          steps.push({
            order:       1,
            action:      "add_auth_guard",
            target:      issue.title,
            description: issue.suggestedFix || "Add security guard",
          });
        }
        break;

      /* ── BUILD / RUNTIME ── */
      case "build":
      case "runtime":
        steps.push({
          order:       1,
          action:      "fix_code",
          target:      "project",
          description: "Use AI Fixer to resolve build/runtime errors",
          meta: {
            useAiFixer: true,
            error:      issue.suggestedFix,
          },
        });
        break;

      /* ── API ── */
      case "api":
        steps.push(
          {
            order:       1,
            action:      "add_rate_limit",
            target:      "api_routes",
            description: "Add rate limiting to prevent API abuse",
          },
          {
            order:       2,
            action:      "fix_code",
            target:      "api_service",
            description: "Add retry logic and circuit breakers",
            meta:        { useAiFixer: true, error: issue.description },
          }
        );
        break;

      /* ── DEPENDENCY ── */
      case "dependency":
        steps.push({
          order:       1,
          action:      "install_dependency",
          target:      issue.title,
          description: issue.suggestedFix || "Install missing package",
        });
        break;

      /* ── PERFORMANCE ── */
      case "performance":
        steps.push({
          order:       1,
          action:      "fix_code",
          target:      "project",
          description: issue.suggestedFix || "Apply performance optimizations",
          meta:        { useAiFixer: true, error: issue.description },
        });
        break;

      /* ── COMPLIANCE ── */
      case "compliance":
        steps.push(
          {
            order:       1,
            action:      "add_auth_guard",
            target:      "sensitive_routes",
            description: "Add compliance-required access controls",
          },
          {
            order:       2,
            action:      "manual_review",
            target:      "compliance_checklist",
            description: "Manual compliance review required - see nuvrahq.com/docs/compliance",
          }
        );
        break;

      /* ── DEFAULT ── */
      default:
        steps.push({
          order:       1,
          action:      "manual_review",
          target:      "project",
          description: issue.suggestedFix || "Manual inspection required",
        });
        break;
    }

    logger.info(`[FixPlanner] Generated ${steps.length} steps for "${issue.title}"`);

    return {
      issueId:         issue.id,
      steps,
      estimatedImpact:
        issue.severity === "critical" ? "high" :
        issue.severity === "major"    ? "medium" : "low",
      requiresReview:  issue.severity === "critical",
      autoExecutable:  !["manual_review"].includes(steps[steps.length - 1]?.action) &&
                       issue.autoFixable,
      category:        issue.category,
    };

  } catch (err) {
    logger.error("[FixPlanner] Failed to generate fix plan", err);

    return {
      issueId:         issue.id,
      steps: [{
        order:       1,
        action:      "manual_review",
        target:      "project",
        description: "Fallback: planner failed - manual review required",
      }],
      estimatedImpact: "low",
      requiresReview:  true,
      autoExecutable:  false,
      category:        issue.category,
    };
  }
}

/**
 * Generate fix plans for a batch of issues.
 * Sorted by severity so critical issues are planned first.
 */
export function generateFixPlans(issues: DetectedIssue[]): FixPlan[] {
  const severityOrder = { critical: 4, major: 3, minor: 2, info: 1 };

  return [...issues]
    .sort((a, b) => (severityOrder[b.severity] ?? 0) - (severityOrder[a.severity] ?? 0))
    .map(issue => generateFixPlan(issue));
}

/**
 * Filter only plans that can be auto-executed without human review.
 */
export function getAutoExecutablePlans(plans: FixPlan[]): FixPlan[] {
  return plans.filter(p => p.autoExecutable && !p.requiresReview);
}